import React, { useEffect, useState } from 'react';
import ModuleProgressRings from '../components/programme/ModuleProgressRings';
import MyBadgesSection from '../components/dashboard/MyBadgesSection';
import UpcomingDeadlines from '../components/dashboard/UpcomingDeadlines';
import ResumeCard from '../components/programme/ResumeCard';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FolderOpen, ArrowRight, Award, CheckCircle2, Clock, Bell, Calendar } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { mediaUrl } from '../lib/mediaUrl';
import AnnouncementsBanner from '../components/dashboard/AnnouncementsBanner';

const API = `${API_BASE}/api`;

const RubricPill = ({ rubric }) => {
  if (!rubric?.approved) return <span className="text-xs text-zinc-500">Assessment score available once the rubric is published</span>;
  return (
    <div className="flex items-center gap-2" data-testid="dashboard-rubric-score">
      <Award className="h-4 w-4 text-navigate-gold" />
      <span className="text-sm text-white font-medium">{Number(rubric.total_score).toFixed(1)} / {rubric.total_points}</span>
      <Badge className={rubric.passed ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-300'}>
        {rubric.passed ? 'Passed' : `Pass mark ${rubric.pass_mark}`}
      </Badge>
    </div>
  );
};

const ProgrammeCard = ({ p, onOpen }) => (
  <Card className="bg-zinc-900 border-zinc-800 hover:border-teal-600/50 transition-colors overflow-hidden" data-testid={`dashboard-programme-${p.programme_id}`}>
    {p.thumbnail_url && (
      <div className="relative h-40 overflow-hidden">
        <img src={mediaUrl(p.thumbnail_url)} alt="" className="w-full h-full object-cover" data-testid={`dashboard-programme-thumbnail-${p.programme_id}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/20 to-transparent" />
      </div>
    )}
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-4">
        <CardTitle className="text-white text-lg leading-snug min-w-0">{p.programme_name}</CardTitle>
        <Button size="sm" onClick={onOpen} className="bg-teal-600 hover:bg-teal-700 text-white shrink-0" data-testid={`dashboard-open-programme-${p.programme_id}`}>
          Open <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      {(p.start_date || p.end_date) && (
        <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-1">
          <Calendar className="h-3.5 w-3.5" /> {p.start_date || '—'} → {p.end_date || '—'}
        </p>
      )}
    </CardHeader>
    <CardContent className="space-y-4">
      <ResumeCard programmeId={p.programme_id} activity={p.next_activity} started={p.completed_activities > 0} compact />
      <div>
        <div className="flex justify-between text-xs text-zinc-400 mb-1.5">
          <span>{p.completed_activities} of {p.total_activities} activities completed</span>
          <span className="text-white font-medium">{p.progress_pct}%</span>
        </div>
        <Progress value={p.progress_pct} className="h-2" />
      </div>
      <ModuleProgressRings programmeId={p.programme_id} modules={p.modules} />
      <RubricPill rubric={p.rubric} />
      <div>
        <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Up next</p>
        {p.upcoming.length === 0 ? (
          <p className="text-sm text-green-400 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> All activities completed</p>
        ) : (
          <ul className="space-y-1.5">
            {p.upcoming.map((a) => (
              <li key={a.activity_id} className="text-sm text-zinc-300 flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                <span className="truncate">{a.title}</span>
                {a.period_title && <span className="text-xs text-zinc-500 shrink-0">· {a.period_title}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </CardContent>
  </Card>
);

const NotificationsCard = ({ items }) => (
  <Card className="bg-zinc-900 border-zinc-800" data-testid="dashboard-notifications">
    <CardHeader className="pb-2"><CardTitle className="text-white text-base flex items-center gap-2"><Bell className="h-4 w-4 text-navigate-gold" /> Notifications</CardTitle></CardHeader>
    <CardContent>
      {items.length === 0 ? <p className="text-sm text-zinc-500">You&apos;re all caught up.</p> : (
        <ul className="space-y-3">
          {items.slice(0, 6).map((n) => (
            <li key={n.notification_id || n.id} className="text-sm">
              {n.link ? <Link to={n.link} className="text-white hover:text-teal-300">{n.title}</Link> : <span className="text-white">{n.title}</span>}
              <p className="text-xs text-zinc-500 line-clamp-2">{n.message}</p>
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);

export default function LearnerProgrammeDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [programmes, setProgrammes] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/learner/programmes`, { headers }).then((r) => setProgrammes(r.data.programmes || [])).catch(() => setProgrammes([]));
    axios.get(`${API}/regulatory/notifications?limit=6`, { headers })
      .then((r) => setNotifications(r.data.notifications || r.data || []))
      .catch(() => setNotifications([]));
  }, [token]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="learner-programme-dashboard">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-1">Welcome back, {user?.full_name?.split(' ')[0] || 'Learner'}</h1>
        <p className="text-zinc-400 mb-10">Here&apos;s where you are in your programmes.</p>
        <AnnouncementsBanner token={token} />
        <UpcomingDeadlines token={token} />
        <MyBadgesSection token={token} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {programmes === null && <div className="h-48 rounded-xl bg-zinc-900 animate-pulse" />}
            {programmes?.length === 0 && (
              <Card className="bg-zinc-900 border-zinc-800 border-dashed" data-testid="dashboard-no-programmes">
                <CardContent className="py-14 text-center">
                  <FolderOpen className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
                  <p className="text-white font-medium">You&apos;re not enrolled in a programme yet</p>
                  <p className="text-sm text-zinc-500 mt-1">Your administrator will enrol you — you&apos;ll see it here.</p>
                </CardContent>
              </Card>
            )}
            {programmes?.map((p) => <ProgrammeCard key={p.programme_id} p={p} onOpen={() => navigate(`/programmes/${p.programme_id}`)} />)}
          </div>
          <NotificationsCard items={Array.isArray(notifications) ? notifications : []} />
        </div>
      </div>
    </Layout>
  );
}
